import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { BehaviorSubject, Observable } from 'rxjs';

interface AvalaraError {
  code: number;
  message: string;
  details?: {
    '@type': string;
    reason: string;
  }[];
}

interface AlertBanner {
  show: boolean;
  message: string;
  type: 'error' | 'warning' | 'info';
}

@Injectable({
  providedIn: 'root',
})
export class TaxPreviewErrorModalService {
  confirmationModal = inject(OpenConfirmationModalService);

  private alertBanner$$ = new BehaviorSubject<AlertBanner>({ show: false, message: '', type: 'error' });
  public alertBanner$: Observable<AlertBanner> = this.alertBanner$$.asObservable();

  constructor(private translate: TranslateService) {}

  static readonly ERROR_MESSAGES = {
    taxCalculationFailed: 'Tax Calculation Failed',
  } as const;

  displayErrorModal(httpErrorResponse: HttpErrorResponse): void {
    const error = httpErrorResponse.error;
    const errorReason = this.extractErrorReason(error);

    if (errorReason) {
      this.openErrorModal(this.translate.instant('BILLING.INVOICE_PAGE.ERROR.' + errorReason));
    } else {
      this.openErrorModal(this.translate.instant('BILLING.INVOICE_PAGE.ERROR.TAX_PROVIDER_GENERIC_ERROR'));
    }
  }

  displayErrorBanner(httpErrorResponse: HttpErrorResponse): void {
    const error = httpErrorResponse.error;
    const errorReason = this.extractErrorReason(error);
    let errorMessage: string;

    if (errorReason) {
      errorMessage = this.translate.instant('BILLING.INVOICE_PAGE.BANNER_ERROR_PREFIX');
      errorMessage += this.translate.instant('BILLING.INVOICE_PAGE.ERROR.' + errorReason);
    } else {
      errorMessage = this.translate.instant('BILLING.INVOICE_PAGE.ERROR.TAX_PROVIDER_GENERIC_ERROR');
    }

    this.showAlertBanner(errorMessage, 'error');
  }

  dismissAlertBanner(): void {
    this.alertBanner$$.next({ show: false, message: '', type: 'error' });
  }

  private showAlertBanner(message: string, type: 'error' | 'warning' | 'info' = 'error'): void {
    this.alertBanner$$.next({ show: true, message, type });
  }

  private openErrorModal(errorMessage: string) {
    this.confirmationModal.openModal({
      type: 'warn',
      title: this.translate.instant('BILLING.INVOICE_PAGE.COULD_NOT_CALCULATE_TAXES'),
      message: errorMessage,
      hideConfirm: true,
      cancelOnEscapeKeyOrBackgroundClick: true,
    });
  }

  private extractErrorReason(error: AvalaraError): string | null {
    if (error?.details && Array.isArray(error.details) && error.details.length > 0) {
      return error.details[0].reason;
    }
    return null;
  }
}
